/* eslint-disable no-inline-styles/no-inline-styles */
import { FC } from "react";
import { useHistory } from "react-router-dom";
import {
  Callout,
  CommandBar,
  DirectionalHint,
  List,
  Nav as OfficeNav,
  SearchBox,
  Stack,
} from "@fluentui/react";
import { ITheme, LoadingOverlay, css, useComponentLogger, useTheme } from "cayo.ui";
import styled from "styled-components";
import { IScheme, INavLink as IAdminNavLink } from "@cayo/shared/api/types/schema";
import { urlUtils } from "@cayo/shared/utils/url";
import { useNav, useToggleNavCollapsed } from "./nav-hooks";
import { useNavTreeSearch } from "./Nav.Logic";
import NavCollapsed from "./NavCollapsed";
import { getSearchBoxStyle, navTreeStyle } from "./NavTree.Style";
import useCallout from "../GlobalHooks/callout.hook";
import Icon from "../Graphics/Icon";
import { onRenderCell } from "../InplaceDateTimeRange/renderers";
import { classNames } from "../InplaceDateTimeRange/styles";

type NavTreeProps = IScheme & {
  groups: IAdminNavLink[];
};

const Nav: FC<NavTreeProps> = (props) => {
  const log = useComponentLogger(Nav);
  const { groups, onLinkExpandHandler, selectedKey, isMedium, resolveName, isLoading } = useNav(
    props.groups
  );
  const { collapsed } = useToggleNavCollapsed();
  const theme = useTheme();

  const { isCalloutVisible, onShowTooltip, onDismiss, tooltipButtonRef, calloutRef } = useCallout();
  const { search, searchResult } = useNavTreeSearch(groups, resolveName);
  const history = useHistory();

  log.debug("props, isMedium", props, isMedium);

  if (isLoading) {
    return <LoadingOverlay />;
  }

  return (
    <NavContainer collapsed={collapsed} isMedium={isMedium}>
      {!collapsed && (
        <Stack horizontal verticalAlign="center" styles={{ root: { padding: "4px 8px" } }}>
          <TooltipButton ref={tooltipButtonRef}>
            <SearchBox
              placeholder={"Search menu"}
              styles={getSearchBoxStyle(theme)}
              style={{}}
              onFocus={() => {
                if (searchResult.length) {
                  onShowTooltip();
                }
              }}
              onChange={(e) => {
                search(e?.currentTarget.value);

                if (e?.currentTarget.value) {
                  onShowTooltip();
                } else {
                  onDismiss();
                }
              }}
            />
          </TooltipButton>
          <CommandBar items={[]} className={css.hidden} />
        </Stack>
      )}
      {isCalloutVisible && !!searchResult.length && (
        <Callout
          target={tooltipButtonRef}
          ref={calloutRef}
          setInitialFocus={false}
          directionalHint={DirectionalHint.bottomCenter}
          onDismiss={onDismiss}
          role="alertdialog"
          layerProps={{ styles: { root: { zIndex: 1000002 } } }} //loading overlay
          styles={{ root: { minWidth: 250 }, calloutMain: { maxWidth: 300 } }}
          //onMouseLeave={onDismiss}
        >
          <List
            className={classNames.optionsList}
            items={searchResult}
            onRenderCell={(item, index) =>
              onRenderCell(item!, index!, true, (item) => {
                onDismiss();
                const newPath = urlUtils.trim(item.value);
                history.push("/" + newPath);
              })
            }
          ></List>
        </Callout>
      )}
      <NonCollapsedNavWrapper>
        {!collapsed ? (
          <OfficeNav
            groups={groups}
            onLinkExpandClick={onLinkExpandHandler}
            selectedKey={selectedKey}
            onRenderLink={(p, r) => {
              if (!p) {
                return null;
              }

              if (p.isCustomIcon) {
                return (
                  <NavWrap compactNoIcon>
                    <Stack
                      verticalAlign={"center"}
                      verticalFill={true}
                      horizontal={true}
                      horizontalAlign={"baseline"}
                      styles={{ root: { height: "100%" } }}
                      tokens={{ childrenGap: 6 }}
                    >
                      <Icon
                        iconName={p.icon}
                        style={{
                          height: 32,
                          fontSize: 16,
                          marginTop: -12,
                          width: 16,
                          color: "inherit",
                        }}
                      />
                      {r!({ ...p, icon: undefined })}
                    </Stack>
                  </NavWrap>
                );
              } else {
                return (
                  <NavWrap compactNoIcon={!p.icon}>
                    {r!(p)}
                    {p.overlayIcon && <Icon iconName={"circleStar"} />}
                  </NavWrap>
                );
              }
            }}
            styles={navTreeStyle(theme)}
          />
        ) : (
          <NavCollapsed
            groups={groups}
            selectedKey={selectedKey}
            expandLink={onLinkExpandHandler}
          ></NavCollapsed>
        )}
      </NonCollapsedNavWrapper>
    </NavContainer>
  );
};

const NavWrap = styled.div<{ compactNoIcon: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: ${(props) => (props.compactNoIcon ? "-22px" : "0")};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const NavContainer = styled.div<{ collapsed: boolean; isMedium: boolean }>`
  height: 100%;
  display: flex;
  width: ${(props) => (props.collapsed ? "50px" : "300px")};
  flex-flow: column;
  flex-shrink: 0;
  justify-content: stretch;
  z-index: 2;
  background-color: ${(props) =>
    (props.theme as ITheme).elementStyles.navBar?.background ||
    (props.theme as ITheme).cayoTheme.brandColors.primaryBackground};
  ${(props) => props.theme.elementStyles.navBar}
`;

const NonCollapsedNavWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  overflow: hidden;
`;

const TooltipButton = styled.div`
  width: 100%;
  flex-grow: 1;
  min-width: 100%;
`;

export default Nav;
